import React, { useState, useEffect } from "react";
import { Package, ShoppingCart, AlertTriangle, ChevronDown, ChevronUp, FileText, Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN } from "../utils/format";

export default function Planeacion() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftItems, setDraftItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [draftForm, setDraftForm] = useState({ supplier_id: "", supplier_name: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [mrpRes, suppRes] = await Promise.all([
        api.get("/mrp/requirements"),
        api.get("/suppliers").catch(() => ({ data: [] })),
      ]);
      setData(mrpRes.data);
      setSuppliers(suppRes.data || []);
    } catch (err) {
      toast.error("Error al cargar requerimientos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openDraftModal = () => {
    if (!data?.raw_materials_summary?.length) {
      toast.error("No hay materiales que comprar");
      return;
    }
    const items = data.raw_materials_summary
      .filter((rm) => rm.to_purchase > 0)
      .map((rm) => ({
        ingredient_id: rm.ingredient_id,
        ingredient_name: rm.ingredient_name,
        quantity: rm.to_purchase,
        unit: rm.unit,
        estimated_cost: 0,
        included: true,
      }));
    if (!items.length) {
      toast.info("Todo el inventario cubre la demanda actual");
      return;
    }
    setDraftItems(items);
    setDraftForm({ supplier_id: "", supplier_name: "", notes: "" });
    setShowDraftModal(true);
  };

  const updateDraftItem = (idx, field, value) => {
    setDraftItems((prev) => prev.map((item, i) =>
      i === idx ? { ...item, [field]: field === "included" ? value : Number(value) || value } : item
    ));
  };

  const selectSupplier = (sid) => {
    const sup = suppliers.find((s) => s.id === sid);
    setDraftForm((f) => ({ ...f, supplier_id: sid, supplier_name: sup?.name || "" }));
  };

  const handleCreateDraft = async () => {
    const included = draftItems.filter((i) => i.included && i.quantity > 0);
    if (!included.length) { toast.error("Selecciona al menos un material"); return; }
    setSaving(true);
    try {
      const res = await api.post("/mrp/draft-purchase", {
        items: included,
        supplier_id: draftForm.supplier_id,
        supplier_name: draftForm.supplier_name,
        notes: draftForm.notes || "Generada desde MRP",
      });
      toast.success(`Orden de compra ${res.data.folio} creada como borrador`);
      setShowDraftModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear orden");
    } finally { setSaving(false); }
  };

  const draftTotal = draftItems
    .filter((i) => i.included)
    .reduce((sum, i) => sum + i.quantity * (i.estimated_cost || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="planeacion-loading">
        <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planeacion-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Planeacion de Materiales</h1>
          <p className="text-slate-500 text-sm mt-1">Calculo automatico de materia prima necesaria para pedidos activos</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="mrp-refresh-btn"
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">
            <RefreshCw size={14} /> Actualizar
          </button>
          <button onClick={openDraftModal} data-testid="mrp-draft-purchase-btn"
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <FileText size={16} /> Generar Orden de Compra
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="kpi-active-orders">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <ShoppingCart size={20} className="text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{data?.active_orders_count || 0}</div>
              <div className="text-xs text-slate-500">Pedidos activos</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="kpi-products-demand">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <Package size={20} className="text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{data?.products_in_demand || 0}</div>
              <div className="text-xs text-slate-500">Productos en demanda</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="kpi-materials-needed">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
              <AlertTriangle size={20} className="text-red-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">
                {(data?.raw_materials_summary || []).filter((r) => r.to_purchase > 0).length}
              </div>
              <div className="text-xs text-slate-500">Materiales por comprar</div>
            </div>
          </div>
        </div>
      </div>

      {/* Resumen de Materias Primas */}
      {data?.raw_materials_summary?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-800">Resumen de Materia Prima Necesaria</h2>
            <p className="text-xs text-slate-500 mt-0.5">Calculado en base al rendimiento (yield %) de cada producto</p>
          </div>
          <table className="w-full text-sm" data-testid="mrp-materials-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Material</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Necesario</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase">En Stock</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Por Comprar</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Usado Por</th>
              </tr>
            </thead>
            <tbody>
              {data.raw_materials_summary.map((rm, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`mrp-material-row-${i}`}>
                  <td className="px-5 py-3 font-medium text-slate-800">{rm.ingredient_name}</td>
                  <td className="px-5 py-3 text-right text-slate-700">{rm.total_needed} {rm.unit}</td>
                  <td className="px-5 py-3 text-right">
                    <span className={rm.current_stock >= rm.total_needed ? "text-emerald-600 font-medium" : "text-amber-600 font-medium"}>
                      {rm.current_stock} {rm.unit}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {rm.to_purchase > 0 ? (
                      <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                        {rm.to_purchase} {rm.unit}
                      </span>
                    ) : (
                      <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                        Cubierto
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500">{[...new Set(rm.used_by)].join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detalle por Producto */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-800">Detalle por Producto</h2>
          <p className="text-xs text-slate-500 mt-0.5">Demanda acumulada de cada producto final y sus pedidos asociados</p>
        </div>
        {(!data?.product_requirements?.length) ? (
          <div className="text-center py-12 text-slate-400 text-sm" data-testid="mrp-no-demand">
            No hay pedidos activos con demanda de producto
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.product_requirements.map((req, idx) => (
              <div key={idx} data-testid={`mrp-product-row-${idx}`}>
                <button
                  className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                  onClick={() => setExpandedProduct(expandedProduct === idx ? null : idx)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600 font-bold text-sm">
                      {req.orders_count}
                    </div>
                    <div>
                      <div className="font-medium text-slate-800">{req.product_name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Demanda total: <strong>{req.total_finished_needed} {req.unit}</strong>
                        {req.yield_pct > 0 && (
                          <span className="ml-2 text-amber-600">
                            | Rendimiento: {req.yield_pct}% | Crudo necesario: ~{req.raw_kg_needed} kg
                          </span>
                        )}
                        {req.purchase_presentation && req.presentations_needed > 0 && (
                          <span className="ml-2 text-blue-600 font-medium">
                            | Comprar: {req.presentations_needed} {req.purchase_presentation}(s) ({req.total_raw_from_presentations} kg)
                          </span>
                        )}
                        {(req.cutting_waste_pct > 0 || req.oven_waste_pct > 0) && (
                          <span className="ml-2 text-slate-400">
                            | Merma: {req.cutting_waste_pct > 0 ? `corte ${req.cutting_waste_pct}%` : ""}{req.cutting_waste_pct > 0 && req.oven_waste_pct > 0 ? " + " : ""}{req.oven_waste_pct > 0 ? `horno ${req.oven_waste_pct}%` : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {req.yield_pct === 0 && (
                      <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                        Sin yield configurado
                      </span>
                    )}
                    {expandedProduct === idx ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                  </div>
                </button>
                {expandedProduct === idx && (
                  <div className="px-5 pb-4 space-y-3">
                    {/* Pedidos que demandan este producto */}
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="text-xs font-semibold text-slate-600 mb-2">Pedidos asociados</div>
                      <div className="space-y-1">
                        {req.orders.map((o, oi) => (
                          <div key={oi} className="flex items-center justify-between text-xs">
                            <span className="font-mono text-slate-700">{o.folio}</span>
                            <span className="text-slate-500">{o.customer_name}</span>
                            <span className="font-medium">{o.qty} {req.unit}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              o.status === "pagado" ? "bg-emerald-100 text-emerald-700" :
                              o.status === "confirmado" ? "bg-blue-100 text-blue-700" :
                              "bg-slate-100 text-slate-600"
                            }`}>{o.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Ingredientes necesarios */}
                    {req.ingredients_detail?.length > 0 && (
                      <div className="bg-amber-50 rounded-lg p-3">
                        <div className="text-xs font-semibold text-amber-800 mb-2">Materia prima requerida</div>
                        <div className="space-y-1.5">
                          {req.ingredients_detail.map((ing, ii) => (
                            <div key={ii} className="flex items-center justify-between text-xs">
                              <span className="font-medium text-slate-700">{ing.ingredient_name}</span>
                              <div className="flex items-center gap-4">
                                <span>Necesario: <strong>{ing.needed} {ing.unit}</strong></span>
                                <span>Stock: <strong className={ing.current_stock >= ing.needed ? "text-emerald-600" : "text-red-600"}>
                                  {ing.current_stock} {ing.unit}
                                </strong></span>
                                {ing.to_purchase > 0 ? (
                                  <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded font-semibold">
                                    Comprar: {ing.to_purchase} {ing.unit}
                                  </span>
                                ) : (
                                  <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-semibold">
                                    Cubierto
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(!req.ingredients_detail || req.ingredients_detail.length === 0) && req.yield_pct === 0 && (
                      <div className="text-xs text-slate-400 italic px-1">
                        Configura el rendimiento (yield %) y los ingredientes del producto para ver calculos de materia prima.
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Draft Purchase Order Modal */}
      {showDraftModal && (
        <Dialog open onOpenChange={() => setShowDraftModal(false)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="draft-purchase-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText size={20} className="text-brand-600" />
                Borrador de Orden de Compra
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-slate-500">
                Revisa y ajusta las cantidades antes de generar la orden. Solo se incluyen materiales con faltante.
              </p>

              {/* Supplier */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Proveedor (opcional)</label>
                  <select value={draftForm.supplier_id}
                    onChange={(e) => selectSupplier(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    data-testid="draft-supplier-select">
                    <option value="">Sin proveedor</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Notas</label>
                  <input value={draftForm.notes}
                    onChange={(e) => setDraftForm((f) => ({ ...f, notes: e.target.value }))}
                    placeholder="Notas opcionales..."
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>

              {/* Items Table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="draft-items-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 w-8"></th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Material</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 w-28">Cantidad</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 w-16">Unidad</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 w-32">Costo unitario</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 w-28">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {draftItems.map((item, idx) => (
                      <tr key={idx} className={`border-t border-slate-100 ${!item.included ? "opacity-40" : ""}`}>
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={item.included}
                            onChange={(e) => updateDraftItem(idx, "included", e.target.checked)}
                            className="rounded border-slate-300" />
                        </td>
                        <td className="px-3 py-2 text-slate-700">{item.ingredient_name}</td>
                        <td className="px-3 py-2">
                          <input type="number" value={item.quantity}
                            onChange={(e) => updateDraftItem(idx, "quantity", e.target.value)}
                            className="w-full border border-slate-200 rounded px-2 py-1 text-xs text-right" min="0" step="0.01" />
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">{item.unit}</td>
                        <td className="px-3 py-2">
                          <input type="number" value={item.estimated_cost}
                            onChange={(e) => updateDraftItem(idx, "estimated_cost", e.target.value)}
                            className="w-full border border-slate-200 rounded px-2 py-1 text-xs text-right" min="0" step="0.01"
                            placeholder="0.00" />
                        </td>
                        <td className="px-3 py-2 text-right text-xs font-medium">
                          {formatMXN(item.quantity * (item.estimated_cost || 0))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center bg-slate-50 rounded-lg px-4 py-3">
                <span className="text-sm text-slate-600">Total estimado:</span>
                <span className="text-lg font-bold text-slate-900">{formatMXN(draftTotal)}</span>
              </div>

              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowDraftModal(false)}
                  className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                  Cancelar
                </button>
                <button onClick={handleCreateDraft} disabled={saving} data-testid="confirm-draft-btn"
                  className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
                  {saving ? "Creando..." : "Crear Borrador de Compra"}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
